import { useState, useEffect, useCallback } from 'react'

import { cacheManager } from './lottie/cacheManager'
import { DEFAULT_LOTTIE_OPTIONS, API_ENDPOINTS } from './lottie/constants'
import { logger } from './lottie/logger'

/**
 * Lottie 애니메이션 데이터를 CDN에서 불러오는 커스텀 훅
 * @param {string} fileName - Lottie JSON 파일 이름
 * @param {object} customOptions - Lottie 커스텀 옵션 (선택사항)
 * @returns {{ data: object | null, error: Error | null, isLoading: boolean, options: object }}
 */
export const useLottieData = (fileName, customOptions = {}) => {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  const options = {
    ...DEFAULT_LOTTIE_OPTIONS,
    ...customOptions
  }

  const fetchLottieData = useCallback(
    async (signal) => {
      if (!fileName) {
        setData(null)
        setError(null)
        setIsLoading(false)
        return
      }

      logger.debug(`[LottieData] 데이터 요청 시작 - ${fileName}`)

      // 캐시 확인
      const cachedData = cacheManager.get(fileName)
      if (cachedData) {
        setData(cachedData)
        setError(null)
        setIsLoading(false)
        return
      }

      try {
        const response = await fetch(API_ENDPOINTS.LOTTIE(fileName), {
          cache: 'force-cache',
          signal
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const jsonData = await response.json()
        cacheManager.set(fileName, jsonData)

        logger.debug(
          `[LottieData] 데이터 로드 완료 - ${fileName}\n`,
          `데이터 크기: ${JSON.stringify(jsonData).length} bytes`
        )

        setData(jsonData)
        setError(null)
      } catch (error) {
        if (error.name === 'AbortError') return

        logger.error('Lottie 데이터를 불러오는데 실패했습니다:', error)
        setError(error)
        setData(null)
      } finally {
        setIsLoading(false)
      }
    },
    [fileName]
  )

  useEffect(() => {
    const controller = new AbortController()
    setIsLoading(true)
    fetchLottieData(controller.signal)

    return () => {
      controller.abort()
    }
  }, [fetchLottieData])

  return { data, error, isLoading, options }
}

export default useLottieData
